import React, { useState, useEffect } from 'react';
import { fetchDiaryEntry, getAllTopicsExercisesStatus, saveDiaryEntry } from '../services/api';
import DiaryNoteForm from './diary/DiaryNoteForm';

const ExerciseMenu = ({ onStartExercise, onBack, selectedTopic }) => {
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [exercisesStatus, setExercisesStatus] = useState(null);
  const [diaryNote, setDiaryNote] = useState('');
  const [diaryLoaded, setDiaryLoaded] = useState(false);
  const [diarySaving, setDiarySaving] = useState(false);
  const [diarySaved, setDiarySaved] = useState(false);

  const topicTitles = {
    'family_hobbies': 'Představování se, koníčky, rodina',
    'shopping_cafe': 'Nakupování, v kavárně, restauraci',
    'travel': 'Cesty, cestování, cizina a cizinci',
    'weather_health': 'Počasí, nemoci, klima, budoucnost',
    'work_internet': 'Práce, Internet, mediální gramotnost'
  };

  // Map exercise types to backend exercise_type names
  const exerciseTypeMap = {
    'conversation': 'conversation_writing',
    'conversation-fillin': 'conversation_fillin',
    'vocabulary': 'vocabulary_fillin',
    'grammar': 'grammar_fillin',
    'speaking': 'speaking',
    'writing': 'writing'
  };

  const exercises = [
    {
      id: 'conversation',
      name: 'Konverzace',
      desc: '3 cvičení k procvičení dialogů',
      icon: '💬',
      color: 'blue',
      required: false,
      subItems: [
        { id: 'conversation', name: 'Dialogy' },
        { id: 'conversation-fillin', name: 'Doplňování' },
        { id: 'speaking', name: 'Mluvení' }
      ]
    },
    {
      id: 'grammar',
      name: 'Gramatika',
      desc: 'Doplňte správné slovo pomocí nápovědy',
      icon: '✓',
      color: 'purple',
      required: false
    },
    {
      id: 'vocabulary',
      name: 'Slovíčka',
      desc: 'Doplňte správné slovíčko do věty',
      icon: '⚡',
      color: 'amber',
      required: false
    },
    {
      id: 'writing',
      name: 'Psaní',
      desc: 'Napište text podle zadání',
      icon: '✏️',
      color: 'rose',
      required: true
    }
  ];

  // Fetch exercises status on mount
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const data = await getAllTopicsExercisesStatus();
        setExercisesStatus(data.topics);
      } catch (error) {
        console.error('Error fetching exercises status:', error);
      }
    };

    fetchStatus();
  }, []);

  useEffect(() => {
    const loadDiaryNote = async () => {
      try {
        const entry = await fetchDiaryEntry(selectedTopic, 'lesson_overview');
        if (entry && entry.note) {
          setDiaryNote(entry.note);
          setDiarySaved(true);
        } else {
          setDiaryNote('');
          setDiarySaved(false);
        }
      } catch (error) {
        console.error('Error fetching diary note:', error);
      } finally {
        setDiaryLoaded(true);
      }
    };

    loadDiaryNote();
  }, [selectedTopic]);

  // Helper function to get exercise status: 'not_started', 'in_progress', or 'finished'
  const getExerciseStatus = (exerciseType) => {
    if (!exercisesStatus || !exercisesStatus[selectedTopic]) {
      return 'not_started';
    }

    const backendExerciseType = exerciseTypeMap[exerciseType];
    const topicStatus = exercisesStatus[selectedTopic];
    const exerciseInfo = topicStatus.exercises.find(ex => ex.exercise_type === backendExerciseType);

    if (!exerciseInfo) return 'not_started';

    if (exerciseInfo.finished) {
      return 'finished';
    }
    if (exerciseInfo.attempts_count > 0 || exerciseInfo.has_progress) {
      return 'in_progress';
    }
    return 'not_started';
  };

  // Get status for exercise or category (with subitems)
  const getCategoryStatus = (exercise) => {
    if (exercise.subItems) {
      const statuses = exercise.subItems.map(sub => getExerciseStatus(sub.id));
      const allFinished = statuses.every(s => s === 'finished');
      const someStarted = statuses.some(s => s === 'finished' || s === 'in_progress');
      if (allFinished) return 'finished';
      if (someStarted) return 'in_progress';
      return 'not_started';
    }
    return getExerciseStatus(exercise.id);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'finished':
        return (
          <div className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        );
      case 'in_progress':
        return (
          <div className="w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center">
            <div className="w-2 h-2 bg-white rounded-full"></div>
          </div>
        );
      default:
        return (
          <div className="w-7 h-7 rounded-full border-2 border-gray-300 flex items-center justify-center">
            <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
          </div>
        );
    }
  };

  const getColorClasses = (color, required) => {
    if (required) {
      return {
        bg: 'bg-rose-500',
        hover: 'hover:bg-rose-600',
        text: 'text-white',
        subtext: 'text-rose-100'
      };
    }
    const colors = {
      purple: { bg: 'bg-white', hover: 'hover:bg-purple-50', border: 'border-l-4 border-purple-500' },
      amber: { bg: 'bg-white', hover: 'hover:bg-amber-50', border: 'border-l-4 border-amber-500' },
      blue: { bg: 'bg-white', hover: 'hover:bg-blue-50', border: 'border-l-4 border-blue-500' },
    };
    return colors[color] || colors.purple;
  };

  const getIconBgClass = (color) => {
    const colors = {
      purple: 'bg-purple-100',
      amber: 'bg-amber-100',
      blue: 'bg-blue-100',
      rose: 'bg-white/20'
    };
    return colors[color] || 'bg-gray-100';
  };

  // Calculate progress
  const getCompletedCount = () => {
    let completed = 0;
    exercises.forEach(ex => {
      if (ex.subItems) {
        ex.subItems.forEach(sub => {
          if (getExerciseStatus(sub.id) === 'finished') completed++;
        });
      } else {
        if (getExerciseStatus(ex.id) === 'finished') completed++;
      }
    });
    return completed;
  };

  const getTotalCount = () => {
    let total = 0;
    exercises.forEach(ex => {
      if (ex.subItems) {
        total += ex.subItems.length;
      } else {
        total++;
      }
    });
    return total;
  };

  const completedCount = getCompletedCount();
  const totalCount = getTotalCount();

  const optionalExercises = exercises.filter(e => !e.required);
  const requiredExercise = exercises.find(e => e.required);

  const handleExerciseClick = (exerciseId) => {
    onStartExercise(exerciseId);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-100 to-gray-200 p-4 md:p-6">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span>Zpět na lekce</span>
        </button>

        {/* Title Card */}
        <div className="bg-white rounded-2xl shadow-lg p-5 mb-6">
          <h1 className="text-xl font-bold text-gray-800 text-center mb-1">
            {topicTitles[selectedTopic] || selectedTopic}
          </h1>

          {/* Circular Progress */}
          <div className="flex items-center justify-center gap-6 mt-4">
            <div className="relative w-20 h-20">
              <svg className="w-20 h-20 transform -rotate-90">
                <circle cx="40" cy="40" r="35" stroke="#e5e7eb" strokeWidth="6" fill="none" />
                <circle
                  cx="40" cy="40" r="35"
                  stroke="#22c55e"
                  strokeWidth="6"
                  fill="none"
                  strokeDasharray={`${(completedCount / totalCount) * 220} 220`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl font-bold text-gray-800">{completedCount}/{totalCount}</span>
              </div>
            </div>
            <div className="text-left">
              <p className="text-gray-500 text-sm">Dokončeno</p>
              <p className="text-lg font-semibold text-gray-700">{completedCount} ze {totalCount} cvičení</p>
            </div>
          </div>
        </div>

        <DiaryNoteForm
          title="Můj deník k této lekci"
          note={diaryNote}
          onNoteChange={setDiaryNote}
          onSave={async () => {
            if (!diaryNote.trim()) {
              return;
            }
            try {
              setDiarySaving(true);
              await saveDiaryEntry({
                topic: selectedTopic,
                noteType: 'lesson_overview',
                note: diaryNote.trim()
              });
              setDiarySaved(true);
            } catch (error) {
              console.error('Error saving diary note:', error);
            } finally {
              setDiarySaving(false);
            }
          }}
          saveLabel={diaryLoaded ? 'Uložit poznámku' : 'Načítám...'}
          inputDisabled={!diaryLoaded || diarySaving}
          buttonDisabled={!diaryLoaded || diarySaving || !diaryNote.trim()}
          statusMessage={diarySaved ? 'Poznámka uložena.' : ''}
        />

        <p className="text-center text-lg text-gray-600 font-medium mt-10 mb-4">Tato cvičení jsou dobrovolná</p>

        {/* Optional Exercises - First */}
        <div className="mb-2 px-1">
          <span className="text-gray-500 font-medium text-sm uppercase tracking-wide">
            Volitelné
          </span>
        </div>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-4">
          {optionalExercises.map((exercise, idx, arr) => (
            <div key={exercise.id}>
              <button
                onClick={() => {
                  if (exercise.subItems) {
                    setExpandedCategory(expandedCategory === exercise.id ? null : exercise.id);
                  } else {
                    handleExerciseClick(exercise.id);
                  }
                }}
                className={`w-full p-4 ${getColorClasses(exercise.color, false).border} ${getColorClasses(exercise.color, false).hover} transition-colors text-left flex items-center gap-4`}
              >
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-2xl ${getIconBgClass(exercise.color)}`}>
                  {exercise.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-800 text-lg">{exercise.name}</h3>
                    {exercise.subItems && (
                      <span className="text-xs text-gray-400">
                        ({exercise.subItems.filter(s => getExerciseStatus(s.id) === 'finished').length}/{exercise.subItems.length})
                      </span>
                    )}
                  </div>
                  <p className="text-gray-500 text-sm truncate">{exercise.desc}</p>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusIcon(getCategoryStatus(exercise))}
                  {exercise.subItems && (
                    <svg
                      className={`w-5 h-5 text-gray-400 transition-transform ${expandedCategory === exercise.id ? 'rotate-180' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </div>
              </button>

              {/* Sub-items for Conversation */}
              {exercise.subItems && expandedCategory === exercise.id && (
                <div className="bg-gray-50 border-l-4 border-blue-500">
                  {exercise.subItems.map((sub, subIdx) => (
                    <button
                      key={subIdx}
                      onClick={() => handleExerciseClick(sub.id)}
                      className="w-full p-3 pl-16 flex items-center justify-between hover:bg-gray-100 transition-colors text-left"
                    >
                      <span className="text-gray-700">{sub.name}</span>
                      {getStatusIcon(getExerciseStatus(sub.id))}
                    </button>
                  ))}
                </div>
              )}

              {idx < arr.length - 1 && <div className="h-px bg-gray-100 ml-4"></div>}
            </div>
          ))}
        </div>

        <p className="text-center text-lg text-gray-600 font-medium mt-10 mb-4">Pro odemknutí další lekce musíte dokončit toto cvičení.</p>

        {/* Required Exercise - At the bottom */}
        {requiredExercise && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2 px-1">
              <span className="text-rose-500 font-bold text-sm uppercase tracking-wide">
                ★ Povinné
              </span>
            </div>
            <button
              onClick={() => handleExerciseClick(requiredExercise.id)}
              className={`w-full rounded-2xl shadow-lg p-5 ${getColorClasses(requiredExercise.color, true).bg} ${getColorClasses(requiredExercise.color, true).hover} transition-all transform hover:scale-[1.02] text-left`}
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center text-3xl">
                  {requiredExercise.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white">{requiredExercise.name}</h3>
                  <p className="text-rose-100 text-sm">{requiredExercise.desc}</p>
                </div>
                <div className="flex flex-col items-center">
                  {getExerciseStatus(requiredExercise.id) === 'not_started' ? (
                    <span className="bg-white/20 text-white text-xs font-medium px-3 py-1 rounded-full">
                      Začít →
                    </span>
                  ) : (
                    getStatusIcon(getExerciseStatus(requiredExercise.id))
                  )}
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Quick Legend */}
        <div className="mt-5 flex justify-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-green-500"></div>
            <span className="text-gray-500">Hotovo</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-amber-500"></div>
            <span className="text-gray-500">Rozpracováno</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full border-2 border-gray-300"></div>
            <span className="text-gray-500">Nezačato</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExerciseMenu;
